import bcrypt from 'bcryptjs';
import { type JSX, useState } from 'react';

import type { BuilderStore } from '../state.js';

import { ToggleField } from './Field.js';

export interface AdminAccessEditorProps {
  store: BuilderStore;
}

/**
 * Admin escape-hatch settings. Cross-field rule: `exitPinHash` is required
 * when `exitGestureEnabled` is true — the validation badge enforces this.
 *
 * PIN handling: the user types plaintext, the Builder bcrypt-hashes it
 * browser-side at cost 10 and stores ONLY the hash in the config. The
 * plaintext is never persisted. This mirrors the production deployment
 * flow — config files end up in support emails and Slack threads;
 * plaintext PINs there would be a vulnerability.
 */
export function AdminAccessEditor({ store }: AdminAccessEditorProps): JSX.Element {
  const { adminAccess } = store.config;
  const [pinDraft, setPinDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function setPin(): Promise<void> {
    const value = pinDraft.trim();
    if (value.length < 4) {
      setFeedback('PIN must be at least 4 characters.');
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const hash = await bcrypt.hash(value, 10);
      store.patchAdminAccess({ exitPinHash: hash });
      setPinDraft('');
      setFeedback('PIN hashed and saved. Plaintext discarded.');
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to hash PIN');
    } finally {
      setBusy(false);
    }
  }

  function clearPin(): void {
    const { exitPinHash: _omit, ...rest } = adminAccess;
    void _omit;
    store.patchAdminAccess(rest);
    setFeedback('PIN cleared.');
  }

  const pinSet = typeof adminAccess.exitPinHash === 'string' && adminAccess.exitPinHash.length > 0;

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Admin access</h2>
        <p className="editor__sub">
          Operator escape hatches. Exit gesture and diagnostic overlay are Pro;
          audit log is Enterprise. The schema accepts every field at every
          tier — runtime decides what to honor.{' '}
          <a href="https://mobilauncher.com/docs/customize#admin-access" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Exit gesture (Pro)</legend>
        <ToggleField
          label="Enable exit gesture"
          hint="Long-press top-left of the launcher to open the PIN modal."
          value={adminAccess.exitGestureEnabled}
          onChange={(value) => store.patchAdminAccess({ exitGestureEnabled: value })}
        />
        {adminAccess.exitGestureEnabled && (
          <div className="pin-flow">
            <div className="pin-flow__status">
              <span className={`pin-flow__pip${pinSet ? ' pin-flow__pip--ok' : ' pin-flow__pip--missing'}`} />
              <span>{pinSet ? 'PIN hash stored' : 'No PIN set — gesture will block validation'}</span>
            </div>
            <label className="field">
              <span className="field__label">Set new PIN</span>
              <div className="pin-flow__row">
                <input
                  className="field__input field__input--mono"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  value={pinDraft}
                  onChange={(e) => setPinDraft(e.target.value)}
                  placeholder="Plaintext — hashed before save"
                />
                <button
                  type="button"
                  className="pin-flow__save"
                  onClick={() => {
                    void setPin();
                  }}
                  disabled={busy || pinDraft.trim().length < 4}
                >
                  {busy ? 'Hashing…' : 'Hash and save'}
                </button>
                {pinSet && (
                  <button
                    type="button"
                    className="pin-flow__clear"
                    onClick={clearPin}
                    disabled={busy}
                  >
                    Clear
                  </button>
                )}
              </div>
              <span className="field__hint">
                Hashed with bcrypt cost 10. Plaintext is never stored in config.
              </span>
            </label>
            {feedback !== null && <p className="pin-flow__feedback">{feedback}</p>}
            {pinSet && (
              <p className="pin-flow__hash">
                Stored hash:{' '}
                <code>
                  {adminAccess.exitPinHash!.slice(0, 12)}…{adminAccess.exitPinHash!.slice(-8)}
                </code>
              </p>
            )}
          </div>
        )}
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Diagnostic + audit</legend>
        <ToggleField
          label="Enable diagnostic overlay (Pro)"
          hint="Long-press bottom-right to view tier, features, audit, telemetry queue."
          value={adminAccess.diagnosticOverlayEnabled}
          onChange={(value) => store.patchAdminAccess({ diagnosticOverlayEnabled: value })}
        />
        <ToggleField
          label="Enable audit log (Enterprise)"
          hint="Admin actions appended to an in-memory ring buffer + telemetry stream."
          value={adminAccess.auditLogEnabled}
          onChange={(value) => store.patchAdminAccess({ auditLogEnabled: value })}
        />
      </fieldset>
    </div>
  );
}
